<template>
    <div class="core-dropdown-wrapper" v-bind="$attrs">
        <Select
            :modelValue="computedValue"
            @update:modelValue="updateValue"
            :name="name"
            :options="getOptions"
            :optionLabel="labelKeyName"
            :placeholder="translatedPlaceholder"
            :filter="filter"
            :class="classes"
            :style="styles"
            :disabled="disabled"
            @show="handleShow"
            @hide="handleHide"
        >
            <template v-if="$slots.valueTemplate" #value="slotProps">
                <slot name="valueTemplate" v-bind="slotProps" />
            </template>

            <template v-if="$slots.optionTemplate" #option="slotProps">
                <slot name="optionTemplate" v-bind="slotProps" />
            </template>
        </Select>

        <CoreInputNotification
            v-if="isInputNotificationShown"
            :validator="validator"
            :customNotificationMessage="customNotificationMessage"
        />
    </div>
</template>

<script>
import Select from 'primevue/select'
import CoreInputNotification from './CoreInputNotification.vue'
import { sortOptionsByLabel } from '@/helpers/sortOptions'

export default {
    name: 'CoreDropdown',

    components: { Select, CoreInputNotification },

    // prevent attribute inheritance on root element
    inheritAttrs: false,

    props: {
        modelValue: {
            type: [String, Number, Object],
            default: '',
        },
        name: {
            type: String,
            default: '',
        },
        options: {
            type: Array,
            default: () => [],
        },
        labelKeyName: {
            type: String,
            default: 'label',
        },
        placeholder: {
            type: String,
            default: '',
        },
        filter: {
            type: Boolean,
            default: false,
        },
        width: {
            type: Number,
            default: null,
        },

        validator: {
            type: Object,
            default: () => null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        customNotificationMessage: {
            type: Object,
            default: () => null,
        },
        touchOnMount: {
            type: Boolean,
            default: false,
        },
        sort: {
            type: Boolean,
            default: true,
        },
    },

    mounted() {
        if (this.validator) {
            if (this.modelValue === null || this.modelValue === '') {
                if (this.touchOnMount) {
                    if (this.validator.$touch) this.validator.$touch()
                } else if (this.validator.$reset) {
                    this.validator.$reset()
                }
            } else {
                if (this.validator.$touch) this.validator.$touch()
            }
        }
    },

    computed: {
        computedValue() {
            const selected = this.getOptions.filter((option) => option.value === this.modelValue)
            return selected.length === 1 ? selected[0] : ''
        },

        classes() {
            return {
                'core-dropdown': true,
                'core-valid': this.isValid,
                'core-invalid': this.isInvalid,
                'core-warning': this.isWarning,
            }
        },

        isValid() {
            if (this.validator !== null && !this.validator.$invalid && this.validator.$dirty) {
                return true
            }

            return false
        },

        isInvalid() {
            if (this.validator !== null && this.validator.$invalid && this.validator.$dirty) {
                return true
            }

            if (
                this.customNotificationMessage !== null &&
                this.customNotificationMessage.severity !== undefined &&
                this.customNotificationMessage.severity === 'error'
            ) {
                return true
            }

            return false
        },

        isWarning() {
            if (
                this.customNotificationMessage !== null &&
                this.customNotificationMessage.severity !== undefined &&
                this.customNotificationMessage.severity === 'warn'
            ) {
                return true
            }

            return false
        },

        validatorErrors() {
            if (this.validator.$error === undefined) {
                return {}
            }
            return this.validator.$error
        },

        validatorParams() {
            if (this.validator.$params === undefined) {
                return {}
            }
            return this.validator.$params
        },

        getOptions() {
            const translated = this.options.map((option) => ({
                value: option.value,
                label: this.$te(option.label) ? this.$t(option.label) : option.label,
                pinned: option.pinned,
            }))
            const ordered = this.sort ? sortOptionsByLabel(translated, this.$i18n?.locale) : translated

            return ordered.map(({ value, label }) => ({ value, label }))
        },

        styles() {
            const stylesObject = {}

            if (this.width !== null && typeof this.width === 'number') {
                stylesObject.width = `${this.width}px`
            }

            return stylesObject
        },

        isInputNotificationShown() {
            if (this.customNotificationMessage !== null) return true

            if (this.validator && this.validator.$dirty) return true

            return false
        },

        translatedPlaceholder() {
            if (!this.placeholder) return ''

            if (this.placeholder && this.placeholder.startsWith('§')) {
                return this.$t(this.placeholder)
            }

            return this.placeholder || ''
        },
    },

    methods: {
        updateValue(selected) {
            this.$emit('update:modelValue', selected.value)

            if (this.validator && this.validator.$touch) {
                this.validator.$touch()
            }
        },

        handleShow() {
            this.$nextTick(() => {
                const input = document.querySelector('.p-select-overlay .p-select-filter')
                if (input) input.focus()
            })
        },

        handleHide() {
            if (this.validator !== null && this.validator.$touch) this.validator.$touch()
        },
    },
}
</script>

<style>
.core-dropdown-wrapper {
    position: relative;
}

.core-dropdown {
    width: 100%;
}

.core-dropdown > span.p-select-label {
    font-size: 1.6rem;
}

.core-dropdown-wrapper > .core-dropdown.p-disabled {
    background-color: var(--color-disabled-bg);
    color: var(--text-disabled);
}

.core-dropdown-wrapper > .core-invalid.p-select.p-component,
.core-dropdown-wrapper > .core-invalid.p-select.p-component:focus,
.core-dropdown-wrapper > .core-invalid.p-select.p-component:hover {
    border: 1px solid var(--color-error);
}

.core-dropdown-wrapper > .core-warning.p-select.p-component,
.core-dropdown-wrapper > .core-warning.p-select.p-component:focus,
.core-dropdown-wrapper > .core-warning.p-select.p-component:hover {
    border: 1px solid var(--color-warning);
}

.core-dropdown-wrapper > .core-valid.p-select.p-component,
.core-dropdown-wrapper > .core-valid.p-select.p-component:focus,
.core-dropdown-wrapper > .core-valid.p-select.p-component:hover {
    border: 1px solid var(--color-success);
}

.p-select-header {
    margin: 4px 0 6px 0;
}

.p-select-overlay .p-select-list-container {
    scrollbar-width: auto;
    scrollbar-color: var(--color-neutral-900) var(--color-neutral-400);
}

.p-select-overlay .p-select-list-container::-webkit-scrollbar-track {
    background: var(--color-neutral-400);
    border-radius: 8px;
}

.p-select-overlay .p-select-list-container::-webkit-scrollbar-thumb {
    background: var(--color-primary-400);
    border-radius: 8px;
    border: 2px solid var(--color-neutral-400);
}

.p-select-overlay .p-select-list-container::-webkit-scrollbar-thumb:hover {
    background: var(--color-primary-500);
}

.p-select-overlay .p-select-list-container::-webkit-scrollbar-thumb:active {
    background: var(--color-primary-600);
}

body .p-select-overlay .p-select-list-container {
    max-height: 20rem !important;
}

.p-select-overlay .p-select-filter {
    font-size: 1.6rem;
}

body .p-select-overlay .p-select-list-container::-webkit-scrollbar {
    width: 20px !important;
}
</style>
